import React, { FC, ReactNode, Suspense, lazy, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';

const HorizontalScrollable = lazy(() => import('../../molecules/HorizontalScrollable'));
import VerticalScrollable from './VerticalScrollable';
import ProductCard from '../ProductCard';
import FleekProductCard from '../../../fleek/pages/ProductPage/ProductCard';

import { SnRProduct } from '../../types/product-page-types';

import styles from './index.pcss';
import QuickLink from '../../molecules/QuickLink';
import { replaceWithJioCDN } from '../../../../src/utils/imageHandling';
import { handleWindowUnload } from '../../../fleek/utils/feedGaUtils';
import { segmentEventPush } from '../../../modules/ga/GTMdatalayer';

interface ProductsLineUpProps {
  products: SnRProduct[];
  title?: ReactNode;
  containerClass?: string;
  titleGutter?: 'sm' | 'lg';
  isPriceReveal?: boolean;
  isPriceRevealDuringSale?: boolean;
  luxe?: boolean | undefined;
  eventPush?: (A: Record<string, string>) => void;
  eventPushObj?: object;
  pageType?: string;
  pageList?: string;
  hasVerticalScroll?: boolean;
  productWidgetCard?: boolean;
  suggestedLinksSimilarTo?: boolean;
  moreLinks?: object;
  addToWishlistApi: () => void;
  setIsWishlisted: (code) => void;
  similarProductsVariant: number | undefined;
  isRecentView?: boolean;
  widgetTitle?: string;
  bannerType?: string;
  ctaSettings?: any;
  viewShowMoreProducts?: () => void;
  dynamicProductWidgetRedirection?: (url: string) => void;
  heading: string | undefined;
  rowNumber: Number;
  banners: any;
  isFleek?: boolean | undefined;
  isSimilarStyle?: boolean | undefined;
  isSimilarStyleDialog?: boolean | undefined;
  fleekPlp?: boolean;
  thumbnailImage?: string;
  baseProduct? : object;
  isFromHomePage?: boolean | undefined;
  isCrossSell?: boolean;
  isFromAjiogramLandingPage: boolean;
  parentProduct? : object;
  priceRevealMetaInfo?: Record<string, unknown>;
}

const ProductsLineUp: FC<ProductsLineUpProps> = (props: ProductsLineUpProps) => {
  const {
    luxe,
    title,
    products,
    containerClass,
    titleGutter = 'lg',
    isPriceReveal = false,
    isPriceRevealDuringSale = false,
    eventPush,
    eventPushObj,
    pageType,
    pageList,
    hasVerticalScroll = false,
    productWidgetCard = false,
    moreLinks,
    suggestedLinksSimilarTo,
    addToWishlistApi,
    setIsWishlisted,
    similarProductsVariant,
    isRecentView,
    widgetTitle = '',
    bannerType,
    ctaSettings = {},
    viewShowMoreProducts,
    dynamicProductWidgetRedirection,
    banners,
    heading,
    rowNumber,
    isFleek = false,
    isSimilarStyle = false,
    isSimilarStyleDialog = false,
    fleekPlp = false,
    thumbnailImage = '',
    exitIntent = false,
    similarProducts,
    isFromHomePage = false,
    isCrossSell,
    baseProduct,
    isFromAjiogramLandingPage = false,
    parentProduct,
    onClickHandler,
    isFromPDP = false,
    isSearchPage = false,
    isJioAdsWidget = false,
    feedLogic = '',
    parentProductObj={},
    position,
    priceRevealMetaInfo = {}
  } = props;

  const parentProductObjRef = useRef(parentProductObj);
  const positionRef = useRef(position);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    parentProductObjRef.current = parentProductObj;
    positionRef.current = position;
  }, [parentProductObj, position]);

  useEffect(() => {
    const itemElement = document.getElementById('jio-ads-widget');
    if (!itemElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const currentObj = parentProductObjRef.current || {};
        const { ad_source = '' } = currentObj;
        if (entry.isIntersecting && ad_source && !hasFiredRef.current) {
          hasFiredRef.current = true;
          segmentEventPush({
            event: 'Widget Viewed',
            screen_name: '',
            widget_id: ad_source === 'osmos' ? 'osmos_pdp_widget' : 'jioads_pdp_widget',
            widget_name: ad_source === 'osmos' ? 'osmos_pdp_widget' : 'jioads_pdp_widget',
            widget_position: positionRef.current,
            widget_type: ad_source === 'osmos' ? 'osmos_pdp_widget' : 'jioads_pdp_widget',
            ...currentObj
          });
          observer.disconnect();
        }
      },
      { threshold: 0.7 }
    );

    observer.observe(itemElement);

    return () => {
      observer.disconnect();
    };
  }, [products]);
  
  const cx = classNames.bind(styles);
  const isDynamicProductList = bannerType === 'DYNAMIC_WIDGET';
  const { enable: isCTAEnabled = false, ctaText = '' } = ctaSettings;
  if (isDynamicProductList && products?.length > 0 && isCTAEnabled) {
    const lastProduct = products[products?.length - 1];
    const isDynamicLinkExists = lastProduct?.showDynamicWidgetMoreLink || false;
    if (!isDynamicLinkExists) {
      products.push({
        ...products[products?.length - 1],
        showDynamicWidgetMoreLink: true
      });
    }
  }

  const renderProductCard = (product, idx) =>
    isFleek ? (
      <FleekProductCard
        widgetTitle={widgetTitle}
        luxe={luxe}
        pageType={pageType || 'pdp'}
        key={
          product?.showDynamicWidgetMoreLink
            ? `products-lineup-${idx}`
            : product.id || `products-lineup-${idx}`
        }
        product={product}
        size="sm"
        // containerClass={styles['product-card']}
        disabled={Boolean(product.disabled)}
        isPriceReveal={isPriceReveal}
        isPriceRevealDuringSale={isPriceRevealDuringSale}
        eventPush={eventPush}
        eventPushObj={eventPushObj}
        index={idx}
        pageList={pageList}
        productWidgetCard={productWidgetCard}
        addToWishlistApi={addToWishlistApi}
        setIsWishlisted={setIsWishlisted}
        similarProductsVariant={similarProductsVariant}
        isRecentView={isRecentView}
        showDynamicWidgetMoreLink={product?.showDynamicWidgetMoreLink || false}
        isDynamicProductList={isDynamicProductList}
        ctaText={ctaText}
        viewShowMoreProducts={viewShowMoreProducts}
        dynamicProductWidgetRedirection={dynamicProductWidgetRedirection}
        heading={heading}
        banners={banners}
        rowNumber={rowNumber}
        isSimilarStyle={isSimilarStyle}
        isSimilarStyleDialog={isSimilarStyleDialog}
        fleekPlp={fleekPlp}
        parentProductObj={parentProductObj}
      />
    ) : (
      <div role='listitem' id={isJioAdsWidget ? `jio-ads-${product?.code}` : ''}
      ><ProductCard
        widgetTitle={widgetTitle}
        luxe={luxe}
        pageType={pageType || 'pdp'}
        key={
          product?.showDynamicWidgetMoreLink
            ? `products-lineup-${idx}`
            : product.id || `products-lineup-${idx}`
        }
        product={product}
        size="sm"
        // containerClass={styles['product-card']}
        disabled={Boolean(product.disabled)}
        isPriceReveal={isPriceReveal}
        isPriceRevealDuringSale={isPriceRevealDuringSale}
        eventPush={eventPush}
        eventPushObj={eventPushObj}
        index={idx}
        pageList={pageList}
        productWidgetCard={productWidgetCard}
        addToWishlistApi={addToWishlistApi}
        setIsWishlisted={setIsWishlisted}
        similarProductsVariant={similarProductsVariant}
        isRecentView={isRecentView}
        showDynamicWidgetMoreLink={product?.showDynamicWidgetMoreLink || false}
        isDynamicProductList={isDynamicProductList}
        ctaText={ctaText}
        viewShowMoreProducts={viewShowMoreProducts}
        dynamicProductWidgetRedirection={dynamicProductWidgetRedirection}
        heading={heading}
        banners={banners}
        rowNumber={rowNumber}
        exitIntent={exitIntent}
        isFromHomePage={isFromHomePage}
        baseProduct={baseProduct}
        isCrossSell={isCrossSell}
        isFromAjiogramLandingPage={isFromAjiogramLandingPage}
        isFromPDP={isFromPDP}
        isSearchPage={isSearchPage}
        isJioAdsWidget = {isJioAdsWidget}
        widgetPosition={position}
        feedLogic={feedLogic}
        parentProductObj={parentProductObj}
        priceRevealMetaInfo={priceRevealMetaInfo}
      /></div>
    );

  const similarToHeader = suggestedLinksSimilarTo ? 'similarToHeader' : '';
  const similarToContainer = suggestedLinksSimilarTo ? 'similarToContainer' : '';

  const containerCls = cx(luxe ? 'luxe-container' : 'container', {
    [containerClass]: !!containerClass,
    similarToContainer
  });

  const fleekTitleGutter = isFleek
    ? isSimilarStyle
      ? 'fleekSimilarStylesTitleGutter'
      : 'fleekTitleGutter'
    : '';

  const titleGutterCls = cx({
    [`title-gutter-${titleGutter}`]: !!titleGutter,
    similarToHeader,
    fleekTitleGutter
  });

  const fleekContainerClass = isFleek
    ? isSimilarStyleDialog
      ? 'fleek-similar-styles-container'
      : 'fleek-recently-container'
    : '';

  return (
    <section className={containerCls} id={similarProducts ? 'similar_products' : ''} onClick={onClickHandler}>
      {isFleek && isSimilarStyleDialog ? (
        <div className={cx('fleekSimalarStyleDialogHeader')}>
          <div className={cx('imageContainer')}>
            <img className={cx('image')} src={replaceWithJioCDN(thumbnailImage)} />
          </div>
          {title ? <header className={titleGutterCls}>{title}</header> : null}
        </div>
      ) : title ? (
        <header className={isFromHomePage ? cx('recently-viewed-heading') : exitIntent ? cx('title-gutter') : titleGutterCls} role='heading' aria-level={2}>{title}</header>
      ) : null}
      {hasVerticalScroll && !luxe ? (
        <VerticalScrollable
          items={products}
          renderItem={renderProductCard}
          containerClassNames={cx('similar-block', { 'similar-pdp': isSimilarStyle })}
          widgetTitle={widgetTitle}
        />
      ) : suggestedLinksSimilarTo && Array.isArray(products) && products.length > 0 ? (
        <div className={cx('similar-content')}>
          <Suspense fallback={null}>
            <HorizontalScrollable
              items={products}
              renderItem={renderProductCard}
              hideScroll
              pageType={pageType}
              luxe={luxe}
              pageList={pageList}
              productWidgetCard={productWidgetCard}
              containerClassNames={!luxe ? cx('recently-container', fleekContainerClass) : ''}
              similarProductsVariant={similarProductsVariant}
              parentProduct = {parentProduct}
              parentProductObj = {parentProductObj}
            />
          </Suspense>
          <div className={cx('suggestedLinks')}>
            <QuickLink
              data={moreLinks?.data}
              luxe={luxe}
              eventPush={eventPush}
              renderAsSuggestedLinks={true}
              productDetails={parentProduct}
            />
          </div>
        </div>
      ) : (
        Array.isArray(products) && products.length > 0 &&
        <div id={isJioAdsWidget ? 'jio-ads-widget' : ''}>
        <Suspense fallback={null}>
          <HorizontalScrollable
            items={products}
            renderItem={renderProductCard}
            hideScroll
            pageType={pageType}
            luxe={luxe}
            pageList={pageList}
            productWidgetCard={productWidgetCard}
            containerClassNames={!luxe ? cx('recently-container', fleekContainerClass) : ''}
            similarProductsVariant={similarProductsVariant}
            widgetTitle={widgetTitle}
            isDynamicProductList={isDynamicProductList}
            banners={banners}
            heading={heading}
            rowNumber={rowNumber}
            exitIntent={exitIntent}
            isCrossSell = {isCrossSell}
            baseProduct = {baseProduct}
            isRoleToContainer={true}
            parentProduct = {parentProduct}
            isSearchPage={isSearchPage}
            parentProductObj = {parentProductObj}
          />
        </Suspense>
        </div>
      )}
    </section>
  );
};

ProductsLineUp.defaultProps = {
  containerClass: 'container',
  titleGutter: 'lg',
  isPriceReveal: false
};

export default ProductsLineUp;
